import { MoreVertRounded } from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ActionItemStatusPicker, ContextLabel, InlineTextField, isWhitespace, LinkBehavior, useDrawer } from "..";
import { DEFAULT_ACTION_ITEM_STATUS, Links } from "../../constants";
import { useBreakpoints } from "../../hooks";
import { useDeleteActionItem, useEditActionItem, useGetActionItemSummary, UserDto } from "../../http";
import { useDestructiveConfirm } from "../../utilities";
import { ActionItemDrawerContent } from "./ActionItemDrawerContent";
import { ActionItemIcons } from "./ActionItemIcons";

const StyledStack = styled(Stack)<{ $isPastView: boolean }>`
  cursor: ${({ $isPastView }) => ($isPastView ? "default" : "pointer")};
  background-color: ${({ $isPastView }) => ($isPastView ? "transparent" : undefined)};
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  .editMenuButton {
    opacity: 0;
  }

  &:hover {
    background-color: ${({ $isPastView, theme }) => ($isPastView ? "transparent" : theme.palette.primary.transparent5)};

    .editMenuButton {
      opacity: 1;
    }
  }
`;

interface IProps {
  actionItemGuid: string;
  canEditPlan: boolean;
  canEdit: boolean;
  showTeamChip?: boolean;
  isPastView?: boolean;
  isFocused?: boolean;
  onBlur?: () => void;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
  sx?: SxProps;
}

const ActionItemCardContent = (props: IProps) => {
  const { showDrawer, closeDrawer } = useDrawer();
  const { data: actionItem, isLoading } = useGetActionItemSummary(props.actionItemGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { mutate: editActionItem } = useEditActionItem();
  const { mutate: deleteActionItem } = useDeleteActionItem();
  const theme = useTheme();
  const destructiveConfirm = useDestructiveConfirm();
  const ref = useRef<HTMLInputElement>(null);
  const { isMdUp } = useBreakpoints();

  useEffect(() => {
    if (props.isFocused) {
      ref.current?.focus();
    }
  }, [props.isFocused]);

  function handleActionItemClicked() {
    showDrawer({
      content: <ActionItemDrawerContent actionItemGuid={props.actionItemGuid} />,
    });
  }

  function handleActionItemBlurred(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (props.canEdit && actionItem) {
      editActionItem({
        actionItemGuid: actionItem.guid,
        planGuid: actionItem.teamPlanSection.planGuid,
        description: e.target.value,
      });
    }

    props.onBlur?.();
  }

  async function handleDeleteClicked(e: React.MouseEvent) {
    setMenuAnchor(null);
    e.stopPropagation();

    const { confirmed } = await destructiveConfirm({
      title: "Delete",
      content: "Are you sure you want to delete this strategic item?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed || isLoading || actionItem === undefined) {
      return;
    }

    // Close the drawer in case it's open.
    closeDrawer();

    deleteActionItem(
      {
        actionItemGuid: actionItem.guid,
        planGuid: actionItem.teamPlanSection.planGuid,
      },
      {
        onSuccess: () => {
          //ToDo: cycle through action item sections and send refetch signals for each active one.
        },
      },
    );
  }

  const shouldDisplayPropertyIcons =
    actionItem !== undefined &&
    (actionItem.owners.length > 0 ||
      (actionItem.note != null && !isWhitespace(actionItem.note)) ||
      actionItem.dueDate != null ||
      actionItem.metricCount > 0 ||
      actionItem.supportingItemsCount > 0 ||
      actionItem.hasAzureDevOpsWorkItems ||
      actionItem.commentCount > 0);

  return (
    <StyledStack
      $isPastView={props.isPastView ?? false}
      direction="row"
      sx={{ alignItems: "center", py: 0, ...props.sx, width: "100%", minWidth: 0 }}
      onClick={() => {
        if (!props.isPastView) {
          handleActionItemClicked();
        }
      }}
    >
      {isLoading || !actionItem ? (
        <Stack sx={{ width: "100%", pl: 1 }}>
          <Skeleton width={"75%"} />
          <Skeleton width={"33%"} />
        </Stack>
      ) : (
        <>
          <Stack spacing={0.2} sx={{ py: 0.25, overflow: "hidden", width: "100%" }}>
            {props.showTeamChip && (
              <Stack direction="row" spacing={0.5} sx={{ pl: 1.75, pt: 0.5 }}>
                <Chip
                  color="primary"
                  component={LinkBehavior}
                  label={actionItem.teamPlanSection!.teamName}
                  href={Links.TeamStrategicMap(actionItem.teamPlanSection!.teamSlug)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  clickable
                />
                <Chip label={actionItem.teamPlanSection!.sectionName} />
              </Stack>
            )}

            <Stack spacing={-0.25} sx={{ overflow: "hidden", width: "100%" }}>
              {actionItem.context && (
                <ContextLabel
                  context={actionItem.context}
                  showIcon
                  hideTeamName
                  disableLink={props.isPastView}
                  condensed
                  sx={{ pl: 1.75 }}
                />
              )}

              <Stack
                direction="row"
                sx={{
                  alignItems: "start",
                  overflow: "hidden",
                  py: props.isPastView ? 0.25 : undefined,
                  width: "100%",
                }}
              >
                <ActionItemStatusPicker
                  variant="button"
                  actionItemGuid={actionItem.guid}
                  status={actionItem.currentStatus ?? DEFAULT_ACTION_ITEM_STATUS}
                  planGuid={actionItem.teamPlanSection.planGuid}
                  disabled={!props.canEdit || props.isPastView}
                  sx={{ position: "relative", top: props.isPastView ? 0 : 4 }}
                />

                {!props.isPastView ? (
                  <InlineTextField
                    ref={ref}
                    value={actionItem.description}
                    onClick={(e) => props.canEdit && e.stopPropagation()}
                    onBlur={handleActionItemBlurred}
                    placeholder="Enter item..."
                    isEditable={props.canEdit}
                    maxLength={512}
                  />
                ) : (
                  <Typography sx={{ ml: 0.25 }}>{actionItem.description}</Typography>
                )}
              </Stack>
            </Stack>

            {shouldDisplayPropertyIcons && <ActionItemIcons actionItem={actionItem} sx={{ pl: 1.75, pb: 0.25 }} />}
          </Stack>

          {isMdUp && (
            <Stack direction="row" spacing={0.25} sx={{ ml: 0.25 }}>
              {props.canEditPlan && (
                <IconButton
                  className="editMenuButton"
                  size="small"
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                    e.stopPropagation();
                  }}
                >
                  <MoreVertRounded />
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={(e: React.PointerEvent) => {
                      setMenuAnchor(null);
                      e.stopPropagation();
                    }}
                  >
                    <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
                      Delete
                    </MenuItem>
                  </Menu>
                </IconButton>
              )}
            </Stack>
          )}
        </>
      )}
    </StyledStack>
  );
};

export { ActionItemCardContent };
